import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Search, X } from 'lucide-react';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';

const UtmImpactForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    impacts,
    categories,
    groups,
    zones,
    fetchImpacts,
    fetchTaxonomy,
    fetchZones,
    createImpact,
    updateImpact,
  } = useUtmTaxonomyStore();

  const isEditMode = Boolean(id);
  const existingImpact = impacts.find((impact) => impact.id === id);

  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    level: 'AUTHORISATION' as
      | 'NOTAM'
      | 'AUTHORISATION'
      | 'NOTIFICATION'
      | 'NOTHING_TO_DO',
    status: 'CHECKED' as 'CHECKED' | 'UNCHECKED',
    intersectionRequirement: 'MUST_BE_WITHIN' as 'MUST_BE_WITHIN' | 'MUST_BE_OUTSIDE',
    linkType: 'ZONE' as 'CATEGORY' | 'GROUP' | 'ZONE',
    linkedIds: [] as string[],
    description: '',
  });

  useEffect(() => {
    void fetchTaxonomy();
    void fetchZones();
    void fetchImpacts();
  }, [fetchTaxonomy, fetchZones, fetchImpacts]);

  useEffect(() => {
    if (!isEditMode || !existingImpact) return;

    setFormData({
      name: existingImpact.name,
      level: existingImpact.level,
      status: existingImpact.status,
      intersectionRequirement: existingImpact.intersectionRequirement ?? 'MUST_BE_WITHIN',
      linkType: existingImpact.linkType,
      linkedIds: existingImpact.linkedIds ?? [],
      description: existingImpact.description || '',
    });
  }, [isEditMode, existingImpact]);

  const availableTargets = useMemo(() => {
    if (formData.linkType === 'CATEGORY') {
      return categories.map((category) => ({
        id: category.id,
        title: category.name,
        subtitle: 'Category',
      }));
    }

    if (formData.linkType === 'GROUP') {
      return groups.map((group) => {
        const category = categories.find((category) => category.id === group.categoryId);
        return {
          id: group.id,
          title: `${group.name} (${group.code})`,
          subtitle: category ? `Category: ${category.name}` : 'Group',
        };
      });
    }

    return zones.map((zone) => {
      const group = groups.find((groupItem) => groupItem.id === zone.groupId);
      return {
        id: zone.id,
        title: `${zone.zoneName} (${zone.zoneCode})`,
        subtitle: group ? `Group: ${group.name}` : 'Zone',
      };
    });
  }, [formData.linkType, categories, groups, zones]);

  const filteredTargets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return availableTargets;
    return availableTargets.filter(
      (target) =>
        target.title.toLowerCase().includes(query) ||
        target.subtitle.toLowerCase().includes(query),
    );
  }, [availableTargets, search]);

  const toggleLinkedTarget = (targetId: string) => {
    setFormData((prev) => {
      const hasTarget = prev.linkedIds.includes(targetId);
      return {
        ...prev,
        linkedIds: hasTarget
          ? prev.linkedIds.filter((idValue) => idValue !== targetId)
          : [...prev.linkedIds, targetId],
      };
    });
  };

  const canSubmit =
    formData.name.trim().length > 0 && formData.linkedIds.length > 0 && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);

    try {
      if (isEditMode && id) {
        await updateImpact(id, formData);
      } else {
        await createImpact(formData);
      }
      navigate('/admin/uzamaps/impacts');
    } catch (error) {
      console.error('Failed to save impact:', error);
      alert('Failed to save impact. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkTypeLabel =
    formData.linkType === 'CATEGORY'
      ? 'categories'
      : formData.linkType === 'GROUP'
      ? 'groups'
      : 'zones';

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex items-start gap-3 sm:items-center sm:gap-4">
        <button
          type="button"
          onClick={() => navigate('/admin/uzamaps/impacts')}
          className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </button>

        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
            {isEditMode ? 'Edit Impact' : 'Create Impact'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure impact severity and choose where this impact applies.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="rounded-lg border border-border bg-card p-4 sm:p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Impact Details</h2>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, name: event.target.value }))
                }
                className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
                placeholder="e.g., Authorisation Required"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Level <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.level}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    level: event.target.value as typeof prev.level,
                  }))
                }
                className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:border-ring focus:outline-none"
                required
              >
                <option value="NOTAM">NOTAM (Highest)</option>
                <option value="AUTHORISATION">Authorisation Required</option>
                <option value="NOTIFICATION">Notification Required</option>
                <option value="NOTHING_TO_DO">Informational Only</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: event.target.value as typeof prev.status,
                  }))
                }
                className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:border-ring focus:outline-none"
                required
              >
                <option value="CHECKED">Checked</option>
                <option value="UNCHECKED">Unchecked</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Intersection requirement <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.intersectionRequirement}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    intersectionRequirement: event.target.value as typeof prev.intersectionRequirement,
                  }))
                }
                className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:border-ring focus:outline-none"
                required
              >
                <option value="MUST_BE_WITHIN">Must be within</option>
                <option value="MUST_BE_OUTSIDE">Must be outside</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Description</label>
              <textarea
                value={formData.description}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, description: event.target.value }))
                }
                className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
                placeholder="Optional description"
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 sm:p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Impact Scope</h2>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-foreground">
              Link impact to <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(['CATEGORY', 'GROUP', 'ZONE'] as const).map((linkType) => (
                <button
                  key={linkType}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      linkType,
                      linkedIds: [],
                    }))
                  }
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    formData.linkType === linkType
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  {linkType === 'CATEGORY'
                    ? 'Category'
                    : linkType === 'GROUP'
                    ? 'Group'
                    : 'Zone'}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Selected {linkTypeLabel}: <span className="font-semibold text-foreground">{formData.linkedIds.length}</span>
            </p>
            {formData.linkedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, linkedIds: [] }))}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X size={12} />
                Clear
              </button>
            )}
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${linkTypeLabel}...`}
              className="w-full rounded-lg border border-border bg-muted py-2 pl-10 pr-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
            />
          </div>

          <div className="max-h-80 overflow-y-auto rounded-lg border border-border bg-background">
            {filteredTargets.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No {linkTypeLabel} found.
              </div>
            ) : (
              filteredTargets.map((target) => {
                const checked = formData.linkedIds.includes(target.id);
                return (
                  <label
                    key={target.id}
                    className="flex cursor-pointer items-start gap-3 border-b border-border p-3 last:border-b-0 hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleLinkedTarget(target.id)}
                      className="mt-1 h-4 w-4"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{target.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{target.subtitle}</p>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/uzamaps/impacts')}
            className="w-full rounded-lg bg-muted px-4 py-2 text-foreground transition-colors hover:bg-accent sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted sm:w-auto"
          >
            <Save size={16} />
            {isSubmitting ? 'Saving...' : 'Save Impact'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UtmImpactForm;
